import React, { useState } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Box,
  Typography,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Divider,
  Paper,
  Fab,
  Tooltip
} from '@mui/material'
import {
  Close as CloseIcon,
  Help as HelpIcon,
  Home as HomeIcon,
  TrendingUp as TrendingUpIcon,
  Settings as SettingsIcon,
  Air as AirIcon,
  Description as DescriptionIcon,
  BugReport as BugReportIcon
} from '@mui/icons-material'
import ReactMarkdown from 'react-markdown'
import userManualContent from '../assets/user-manual.md?raw'

interface UserManualProps {
  open: boolean
  onClose: () => void
}

interface TableOfContents {
  id: string
  title: string
  icon: React.ReactElement
}

const tableOfContents: TableOfContents[] = [
  { id: 'system-overview', title: '시스템 개요', icon: <HomeIcon /> },
  { id: 'getting-started', title: '시작하기', icon: <TrendingUpIcon /> },
  { id: 'airflow-selection', title: '풍량 선정 탭', icon: <AirIcon /> },
  { id: 'equipment-standards', title: '장비 기준 탭', icon: <SettingsIcon /> },
  { id: 'fan-selection', title: '팬 선정 탭', icon: <AirIcon /> },
  { id: 'output-sheets', title: '출력 시트 탭', icon: <DescriptionIcon /> },
  { id: 'troubleshooting', title: '문제 해결', icon: <BugReportIcon /> }
]

const UserManual: React.FC<UserManualProps> = ({ open, onClose }) => {
  const [selectedSection, setSelectedSection] = useState<string>('system-overview')

  // 마크다운에서 특정 섹션 추출
  const extractSection = (content: string, sectionId: string): string => {
    const lines = content.split('\n')
    const sectionTitles: { [key: string]: string } = {
      'system-overview': '## 시스템 개요',
      'getting-started': '## 시작하기',
      'airflow-selection': '## 풍량 선정 탭',
      'equipment-standards': '## 장비 기준 탭',
      'fan-selection': '## 팬 선정 탭',
      'output-sheets': '## 출력 시트 탭',
      'troubleshooting': '## 문제 해결'
    }

    const startTitle = sectionTitles[sectionId]
    if (!startTitle) return content

    const startIndex = lines.findIndex(line => line.trim() === startTitle)
    if (startIndex === -1) return content

    // 다음 ## 헤더를 찾기
    let endIndex = lines.findIndex((line, idx) =>
      idx > startIndex && line.trim().startsWith('## ') && !line.includes('---')
    )

    if (endIndex === -1) endIndex = lines.length

    return lines.slice(startIndex, endIndex).join('\n')
  }

  const currentContent = extractSection(userManualContent, selectedSection)

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: { height: '90vh' }
      }}
    >
      <DialogTitle sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        borderBottom: 1,
        borderColor: 'divider',
        bgcolor: 'primary.main',
        color: 'white'
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <HelpIcon />
          <Typography variant="h6">사용자 메뉴얼</Typography>
        </Box>
        <IconButton onClick={onClose} sx={{ color: 'white' }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 0, display: 'flex', height: 'calc(100% - 64px)' }}>
        {/* 좌측 목차 */}
        <Paper
          sx={{
            width: 280,
            borderRight: 1,
            borderColor: 'divider',
            overflowY: 'auto',
            bgcolor: 'grey.50'
          }}
          elevation={0}
        >
          <Box sx={{ p: 2 }}>
            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1, fontWeight: 'bold' }}>
              목차
            </Typography>
            <List disablePadding>
              {tableOfContents.map((item, index) => (
                <React.Fragment key={item.id}>
                  <ListItem disablePadding>
                    <ListItemButton
                      selected={selectedSection === item.id}
                      onClick={() => setSelectedSection(item.id)}
                      sx={{
                        borderRadius: 1,
                        mb: 0.5,
                        '&.Mui-selected': {
                          bgcolor: 'primary.main',
                          color: 'white',
                          '&:hover': {
                            bgcolor: 'primary.dark'
                          },
                          '& .MuiListItemIcon-root': {
                            color: 'white'
                          }
                        }
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, width: '100%' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', minWidth: 24 }}>
                          {React.cloneElement(item.icon, {
                            fontSize: 'small',
                            sx: { color: selectedSection === item.id ? 'inherit' : 'action.active' }
                          })}
                        </Box>
                        <ListItemText
                          primary={item.title}
                          primaryTypographyProps={{
                            fontSize: '0.9rem',
                            fontWeight: selectedSection === item.id ? 'bold' : 'normal'
                          }}
                        />
                      </Box>
                    </ListItemButton>
                  </ListItem>
                </React.Fragment>
              ))}
            </List>
          </Box>
        </Paper>

        {/* 우측 컨텐츠 */}
        <Box sx={{
          flex: 1,
          overflowY: 'auto',
          p: 4
        }}>
          <Paper elevation={0} sx={{
            '& h2': {
              fontSize: '1.75rem',
              fontWeight: 'bold',
              mb: 2,
              mt: 3,
              color: 'primary.main',
              borderBottom: 2,
              borderColor: 'primary.main',
              pb: 1
            },
            '& h3': {
              fontSize: '1.25rem',
              fontWeight: 'bold',
              mb: 1.5,
              mt: 2.5,
              color: 'text.primary'
            },
            '& h4': {
              fontSize: '1.1rem',
              fontWeight: 'bold',
              mb: 1,
              mt: 2,
              color: 'text.secondary'
            },
            '& p': {
              mb: 1.5,
              lineHeight: 1.7,
              color: 'text.primary'
            },
            '& ul': {
              mb: 2,
              pl: 3
            },
            '& li': {
              mb: 0.5,
              lineHeight: 1.6
            },
            '& code': {
              bgcolor: 'grey.100',
              color: 'error.main',
              px: 0.5,
              py: 0.25,
              borderRadius: 0.5,
              fontSize: '0.9em',
              fontFamily: 'monospace'
            },
            '& pre': {
              bgcolor: 'grey.900',
              color: 'grey.100',
              p: 2,
              borderRadius: 1,
              overflow: 'auto',
              mb: 2
            },
            '& blockquote': {
              borderLeft: 4,
              borderColor: 'primary.main',
              pl: 2,
              ml: 0,
              color: 'text.secondary',
              fontStyle: 'italic'
            },
            '& hr': {
              my: 3,
              border: 0,
              borderTop: 1,
              borderColor: 'divider'
            },
            '& table': {
              width: '100%',
              mb: 2,
              borderCollapse: 'collapse'
            },
            '& th': {
              bgcolor: 'primary.main',
              color: 'white',
              p: 1,
              textAlign: 'left',
              fontWeight: 'bold'
            },
            '& td': {
              border: 1,
              borderColor: 'divider',
              p: 1
            },
            '& tr:nth-of-type(even)': {
              bgcolor: 'grey.50'
            }
          }}>
            <ReactMarkdown>{currentContent}</ReactMarkdown>
          </Paper>
        </Box>
      </DialogContent>
    </Dialog>
  )
}

// 플로팅 도움말 버튼 컴포넌트
export const UserManualButton: React.FC = () => {
  const [open, setOpen] = useState(false)

  return (
    <>
      <Tooltip title="사용자 메뉴얼" placement="left">
        <Fab
          color="primary"
          onClick={() => setOpen(true)}
          sx={{
            position: 'fixed',
            bottom: 24,
            right: 24,
            zIndex: 1000
          }}
        >
          <HelpIcon />
        </Fab>
      </Tooltip>

      <UserManual open={open} onClose={() => setOpen(false)} />
    </>
  )
}

export default UserManual
